/* @jsx h */
import { h } from "vue";
import type { GridSchema } from "@lemmonhq/uikit";
import type { StockItem } from "./types";
import { formatQty } from "./formatQty";

type Translate = (key: string) => string;

const money = (value: number | null) =>
  value == null ? "—" : value.toFixed(value < 1 ? 4 : 2);

export const buildStockSchema = (
  t: Translate,
  onEditUnits: (item: StockItem) => void,
  onHistory: (item: StockItem) => void,
): GridSchema<StockItem> => ({
  primaryKey: "id",
  columns: [
    {
      key: "name",
      header: t("inventory.stock.col_name"),
      type: "text",
      width: 240,
      frozen: true,
      editable: true,
      required: true,
      cellClass: "font-semibold",
    },
    {
      key: "stock_category",
      header: t("inventory.stock.col_category"),
      type: "text",
      width: 150,
      editable: true,
      bulkEditable: true,
    },
    {
      key: "qty_on_hand",
      header: t("inventory.stock.col_on_hand"),
      type: "readonly",
      width: 190,
      formatter: (_value, row) => formatQty(row.qty_on_hand, row.base_unit, row.units),
      cellClass: (_value, row) =>
        row.par_level != null && row.qty_on_hand < row.par_level
          ? "text-warning font-medium"
          : "",
    },
    {
      key: "par_level",
      header: t("inventory.stock.col_par"),
      type: "number",
      width: 110,
      editable: true,
      bulkEditable: true,
      min: 0,
    },
    {
      key: "base_unit",
      header: t("inventory.stock.col_base_unit"),
      type: "readonly",
      width: 100,
    },
    {
      key: "units",
      header: t("inventory.stock.col_units"),
      type: "readonly",
      width: 230,
      renderCell: (_value, row) => (
        <button
          class="btn btn-ghost btn-xs w-full justify-start font-normal"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onEditUnits(row);
          }}
        >
          <span class="material-symbols-outlined text-sm">straighten</span>
          <span class="truncate">
            {row.units.length
              ? row.units.map((u) => `${u.name} = ${u.base_qty} ${row.base_unit}`).join(", ")
              : t("inventory.stock.add_units")}
          </span>
        </button>
      ),
    },
    {
      key: "__history",
      header: t("inventory.stock.movements"),
      type: "readonly",
      width: 120,
      renderCell: (_value, row) => (
        <button
          class="btn btn-ghost btn-xs w-full justify-start font-normal"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onHistory(row);
          }}
        >
          <span class="material-symbols-outlined text-sm">history</span>
          {t("inventory.stock.history")}
        </button>
      ),
    },
    {
      key: "avg_cost",
      header: t("inventory.stock.col_avg_cost"),
      type: "readonly",
      width: 130,
      formatter: (value) => money(value),
    },
    {
      key: "last_cost",
      header: t("inventory.stock.col_last_cost"),
      type: "readonly",
      width: 130,
      formatter: (value) => money(value),
    },
    {
      key: "active",
      header: t("inventory.stock.col_active"),
      type: "boolean",
      width: 90,
      editable: true,
      bulkEditable: true,
    },
  ],
});
